import { useEffect, useId, useRef, useState } from "react";
import { useFetcher, useLoaderData, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import type { ApprovalRules } from "@edm/contracts";
import { Loading } from "../../components/skeletons.jsx";
import { Deferred } from "../../components/states.jsx";
import { translate, useI18n } from "../../i18n/index.js";
import { isApiError } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { PageHeader, Select, SettingsRow, SettingsSection, SwitchControl, useToast, Skeleton } from "../../ui/index.js";

/**
 * Settings: the approval rules.
 *
 * The approval rules are the ones core-engine stores and counts votes against
 * (D-0016); this page reads and writes them and never judges a vote itself.
 * Self-approval is a team setting, not a product principle — the invariant
 * that binds is that an agent's approval never counts, and that is stated as a
 * fact below, not offered as a switch.
 */

const CLASSES = ["create", "amend", "retire"] as const;
const COUNTS = ["1", "2", "3", "4", "5"] as const;

export function approvalRulesLoader({ context }: LoaderFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  return { rules: deferred(api.approvalRules()) };
}

export async function approvalRulesAction({ context, request }: ActionFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  const form = await request.formData();
  const count = (k: string) => Number(form.get(k) ?? 1);
  try {
    await api.setApprovalRules({
      allowSelfApproval: form.get("allowSelfApproval") === "true",
      createApprovals: count("createApprovals"),
      amendApprovals: count("amendApprovals"),
      retireApprovals: count("retireApprovals"),
      revision: Number(form.get("revision") ?? 0),
    });
    return { ok: translate("settings.approval.saved") } satisfies ActionResult;
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "conflict") || isApiError(err, "forbidden")) return { error: err.message } satisfies ActionResult;
    throw err;
  }
}

type ActionResult = { ok?: string; error?: string };
type Draft = { allowSelfApproval: boolean; create: number; amend: number; retire: number };
const fromRules = (r: ApprovalRules): Draft => ({ allowSelfApproval: r.allowSelfApproval, create: r.createApprovals, amend: r.amendApprovals, retire: r.retireApprovals });

/**
 * Every change saves at once, and the control shows it before the engine answers.
 * A change made while a save is in flight waits for it and goes out next, against the
 * revision that save produced; a refused save puts the stored rules back.
 */
function ApprovalRulesSettings({ rules }: { rules: ApprovalRules }) {
  const { t, tPlural } = useI18n();
  const toast = useToast();
  const fetcher = useFetcher<ActionResult>();
  const selfApproveId = useId();
  const [draft, setDraft] = useState(() => fromRules(rules));
  const latest = useRef(draft);
  const queued = useRef<Draft | null>(null);
  const announced = useRef<ActionResult>(undefined);
  const items = COUNTS.map((n) => ({ value: n, label: tPlural("settings.approval.people", Number(n)) }));

  const submit = (next: Draft, revision: number) =>
    fetcher.submit(
      { allowSelfApproval: String(next.allowSelfApproval), createApprovals: next.create, amendApprovals: next.amend, retireApprovals: next.retire, revision },
      { method: "post" },
    );

  const change = (patch: Partial<Draft>) => {
    const next = { ...latest.current, ...patch };
    latest.current = next;
    setDraft(next);
    if (fetcher.state === "idle") submit(next, rules.revision);
    else queued.current = next;
  };

  // By the time the fetcher is idle again the loader has revalidated, so `rules` is what the engine stored.
  useEffect(() => {
    const result = fetcher.data;
    if (fetcher.state !== "idle" || !result || announced.current === result) return;
    announced.current = result;
    if (result.error) {
      queued.current = null;
      latest.current = fromRules(rules);
      setDraft(latest.current);
      toast.danger(result.error);
    } else if (queued.current) {
      const next = queued.current;
      queued.current = null;
      submit(next, rules.revision);
    } else {
      toast.success(t("settings.approval.saved"));
    }
  });

  return (
    <SettingsSection description={rules.revision === 0 ? t("settings.approval.defaults") : undefined}>
      <SettingsRow label={t("settings.approval.selfApprove")} labelFor={selfApproveId}>
        <SwitchControl id={selfApproveId} checked={draft.allowSelfApproval} onCheckedChange={(allowSelfApproval) => change({ allowSelfApproval })} />
      </SettingsRow>
      {CLASSES.map((c) => (
        <SettingsRow key={c} label={t(`settings.approval.${c}`)}>
          <Select
            items={items}
            value={String(draft[c]) as (typeof COUNTS)[number]}
            onValueChange={(v) => change({ [c]: Number(v) })}
            aria-label={t(`settings.approval.${c}`)}
          />
        </SettingsRow>
      ))}
    </SettingsSection>
  );
}

function ApprovalRulesSkeleton() {
  const { t } = useI18n();
  return (
    <Loading>
      <SettingsSection>
        <SettingsRow label={t("settings.approval.selfApprove")}>
          <Skeleton shape="switch" />
        </SettingsRow>
        {CLASSES.map((c) => (
          <SettingsRow key={c} label={t(`settings.approval.${c}`)}>
            <Skeleton shape="select" />
          </SettingsRow>
        ))}
      </SettingsSection>
    </Loading>
  );
}

/** What no team setting changes — stated as fact, not offered as a switch. */
function FixedRules() {
  const { t } = useI18n();
  return (
    <SettingsSection title={t("settings.approval.fixed")}>
      <SettingsRow label={t("settings.approval.agents")} description={t("settings.approval.agentsValue")} />
      <SettingsRow label={t("settings.approval.blocking")} description={t("settings.approval.blockingValue")} />
    </SettingsSection>
  );
}

export function ApprovalRulesPageSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("settings.approval.title")} description={t("settings.approval.description")} />
      <ApprovalRulesSkeleton />
      <FixedRules />
    </>
  );
}

export function ApprovalRulesRoute() {
  const { rules } = useLoaderData<typeof approvalRulesLoader>();
  const { t } = useI18n();

  return (
    <>
      <PageHeader title={t("settings.approval.title")} description={t("settings.approval.description")} />
      <Deferred data={rules} fallback={<ApprovalRulesSkeleton />}>
        {(rules) => <ApprovalRulesSettings rules={rules} />}
      </Deferred>
      <FixedRules />
    </>
  );
}
